// File: src/components/admin/promos/CreatePromoCodeDialog.js
// Create a brand-new promotion code. Mirrors CreatePlanDialog.js.
//
// Fields: code (with a Generate button + client-side validation), description,
// durationDays, maxUses (blank = unlimited → null/omit), expiresAt
// (datetime-local → ms epoch), planId (default 'pro').

import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Stack, Alert, CircularProgress, InputAdornment, MenuItem,
} from '@mui/material';
import CasinoIcon from '@mui/icons-material/Casino';
import { mainApi } from '../../../services/mainApi';

// Human-readable label for a plan option: displayName → name → id, annotated
// with the raw id so admins can disambiguate (e.g. "Pro Plan (pro)").
function planLabel(plan) {
    const name = plan.displayName || plan.name || plan.id;
    return name === plan.id ? name : `${name} (${plan.id})`;
}

// Default selection: 'pro' if present, else the first non-'free' plan, else the
// first plan in the (already sortOrder-sorted) list.
function pickDefaultPlan(plans) {
    if (plans.some(p => p.id === 'pro')) return 'pro';
    const nonFree = plans.find(p => p.id !== 'free');
    if (nonFree) return nonFree.id;
    return plans[0]?.id || '';
}

// Same shape the backend enforces: starts with [A-Z0-9], then [A-Z0-9-],
// total length 3..24.
const CODE_RE = /^[A-Z0-9][A-Z0-9-]{2,23}$/;
const CODE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

function generateCode(len = 8) {
    let out = '';
    for (let i = 0; i < len; i++) {
        out += CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)];
    }
    return out;
}

export default function CreatePromoCodeDialog({ open, onClose, onCreated }) {
    const [code, setCode] = useState('');
    const [description, setDescription] = useState('');
    const [durationDays, setDurationDays] = useState(30);
    const [maxUses, setMaxUses] = useState('');
    const [expiresAt, setExpiresAt] = useState('');
    const [planId, setPlanId] = useState('');
    const [plans, setPlans] = useState([]);
    const [plansLoading, setPlansLoading] = useState(false);
    const [plansError, setPlansError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Fetch the real plans when the dialog opens so the option list is fresh
    // relative to any plan edits. Archived plans can't be granted, so exclude
    // them. If the fetch fails or yields no selectable plans, surface an inline
    // error and (via `canCreate`) keep the Create button disabled.
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setPlansLoading(true);
        setPlansError(null);
        (async () => {
            try {
                const all = await mainApi.listPlans();
                if (cancelled) return;
                const selectable = (Array.isArray(all) ? all : []).filter(p => !p.archived);
                setPlans(selectable);
                if (selectable.length === 0) {
                    setPlansError('No selectable plans available — a promo must grant a real plan.');
                } else {
                    setPlanId(pickDefaultPlan(selectable));
                }
            } catch (e) {
                if (cancelled) return;
                setPlans([]);
                setPlansError(e?.response?.data?.error || e.message || 'Failed to load plans.');
            } finally {
                if (!cancelled) setPlansLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open]);

    function reset() {
        setCode(''); setDescription(''); setDurationDays(30); setMaxUses('');
        setExpiresAt(''); setPlanId(''); setError(null); setSaving(false);
        setPlans([]); setPlansError(null); setPlansLoading(false);
    }

    const canCreate = !saving && !!code && !plansLoading && !plansError && !!planId;

    async function handleCreate() {
        if (!CODE_RE.test(code)) {
            setError('Code must start with [A-Z0-9] and contain only [A-Z0-9-] (3-24 chars, uppercase).');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const body = {
                code,
                description,
                durationDays: Number(durationDays),
            };
            if (planId) body.planId = planId;
            // Blank maxUses == unlimited → send null so the BE stores no cap.
            body.maxUses = maxUses === '' ? null : Number(maxUses);
            // datetime-local string → ms epoch; blank == no expiry.
            if (expiresAt) body.expiresAt = new Date(expiresAt).getTime();
            const promo = await mainApi.createPromoCode(body);
            reset();
            onClose();
            if (onCreated) onCreated(promo);
        } catch (e) {
            setError(e?.response?.data?.error || e.message);
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth
            data-testid="create-promo-dialog">
            <DialogTitle>New promo code</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    {error && <Alert severity="error" data-testid="create-promo-error">{error}</Alert>}
                    <TextField
                        label="Code"
                        size="small"
                        value={code}
                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                        helperText="Uppercase, [A-Z0-9-], starts with a letter/digit (3-24 chars)."
                        inputProps={{ 'data-testid': 'create-promo-code' }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Button
                                        size="small"
                                        startIcon={<CasinoIcon />}
                                        onClick={() => setCode(generateCode())}
                                        data-testid="create-promo-generate"
                                    >
                                        Generate
                                    </Button>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        label="Description"
                        size="small"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        inputProps={{ 'data-testid': 'create-promo-description' }}
                    />
                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Duration (days)"
                            type="number"
                            size="small"
                            value={durationDays}
                            onChange={(e) => setDurationDays(Number(e.target.value))}
                            sx={{ width: 160 }}
                            inputProps={{ 'data-testid': 'create-promo-durationDays', min: 0 }}
                        />
                        <TextField
                            label="Max uses"
                            type="number"
                            size="small"
                            value={maxUses}
                            onChange={(e) => setMaxUses(e.target.value)}
                            sx={{ width: 160 }}
                            helperText="Blank = unlimited"
                            inputProps={{ 'data-testid': 'create-promo-maxUses', min: 0 }}
                        />
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Expires at"
                            type="datetime-local"
                            size="small"
                            value={expiresAt}
                            onChange={(e) => setExpiresAt(e.target.value)}
                            sx={{ width: 240 }}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ 'data-testid': 'create-promo-expiresAt' }}
                        />
                        <TextField
                            select
                            label="Plan"
                            size="small"
                            value={plansLoading || plansError ? '' : planId}
                            onChange={(e) => setPlanId(e.target.value)}
                            disabled={plansLoading || !!plansError || plans.length === 0}
                            sx={{ width: 220 }}
                            error={!!plansError}
                            helperText={
                                plansLoading ? 'Loading plans…'
                                    : plansError ? plansError
                                        : 'Plan this code grants'
                            }
                            inputProps={{ 'data-testid': 'create-promo-planId-select' }}
                        >
                            {plans.map(p => (
                                <MenuItem key={p.id} value={p.id} data-testid={`create-promo-planId-option-${p.id}`}>
                                    {planLabel(p)}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => { reset(); onClose(); }} disabled={saving}
                    data-testid="create-promo-cancel">
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleCreate} disabled={!canCreate}
                    data-testid="create-promo-submit">
                    {saving ? <CircularProgress size={20} /> : 'Create code'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
